import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RefreshCw, ChevronLeft, ChevronRight, Download, Undo2, Loader2, Save, X } from 'lucide-react';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { listRequestCleanup, saveRequestOverrides, revertRequestOverrides, validateCleanupInput, cleanupValueToText, cleanupValuesEqual, dateSortKey, type CleanupActor, type CleanupFieldSpec, type CleanupRow, type CleanupSource, type CleanupValue } from '../../utils/requestCleanup';
import { fetchDeals } from '../../utils/database';
import { transformRequestResponse } from '../../utils/transformers';
import { useRequests } from '../../context/RequestsContext';
import { friendlyTime } from '../../utils/friendlyTime';
import type { Request } from '../../types';
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
export interface CleanupDataSource {
  list: typeof listRequestCleanup;
  save: typeof saveRequestOverrides;
  revert: typeof revertRequestOverrides;
  /** Re-fetches the dashboard's requests (merged with overrides) after a change. */
  fetchRequests: () => Promise<Request[] | null>;
}
const DEFAULT_DATA_SOURCE: CleanupDataSource = {
  list: listRequestCleanup,
  save: saveRequestOverrides,
  revert: revertRequestOverrides,
  fetchRequests: async () => {
    const result = await fetchDeals();
    return result ? transformRequestResponse(result.records) : null;
  }
};
type Drafts = Record<string, Record<string, string>>; // requestID -> field -> staged text

function SourceBadge({
  row
}: {
  row: CleanupRow;
}) {
  const overridden = row.overriddenFields.length > 0;
  const tone = overridden ? 'bg-indigo-100 text-indigo-700' : row.source === 'uploaded' ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600';
  const label = overridden ? 'Overridden' : row.source === 'uploaded' ? 'Uploaded' : 'Synced';
  return <span data-testid="source-badge" title={overridden ? `${row.source === 'uploaded' ? 'Uploaded' : 'Synced'} row with ${row.overriddenFields.length} manual override(s)` + (row.updatedBy ? ` · last edit by ${row.updatedBy}` : '') : undefined} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${tone}`} data-id="element-344" data-mp="2q0dhyakmzp62">
      {label}
    </span>;
}
interface CellProps {
  row: CleanupRow;
  spec: CleanupFieldSpec;
  draft: string | undefined;
  editing: boolean;
  disabled: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onChange: (text: string) => void;
  onCancelDraft: () => void;
  onRevertField: () => void;
}

// Module-level component (stable identity) so an input keeps focus while the
// parent re-renders on every keystroke.
function CleanupCell({
  row,
  spec,
  draft,
  editing,
  disabled,
  onStartEdit,
  onStopEdit,
  onChange,
  onCancelDraft,
  onRevertField
}: CellProps) {
  const current = row.merged[spec.name];
  const currentText = cleanupValueToText(current);
  const overridden = row.overriddenFields.includes(spec.name);
  const differs = row.differsFields.includes(spec.name);
  const staged = draft !== undefined;
  const shownText = staged ? draft : currentText;
  const validation = staged ? validateCleanupInput(spec, draft, row.base[spec.name]) : null;
  const invalid = validation !== null && !validation.ok;
  if (spec.readOnly || disabled) {
    return <span className="text-xs text-slate-700 whitespace-nowrap" data-id="element-345" data-mp="319p7n450ke16">{currentText || <span className="text-slate-300" data-id="element-346" data-mp="2x6anc1a1lbhx">—</span>}</span>;
  }
  if (editing) {
    const common = {
      autoFocus: true,
      value: shownText,
      onChange: (e: {
        target: {
          value: string;
        };
      }) => onChange(e.target.value),
      onBlur: onStopEdit,
      onKeyDown: (e: {
        key: string;
      }) => {
        if (e.key === 'Enter') onStopEdit();
        if (e.key === 'Escape') {
          onCancelDraft();
          onStopEdit();
        }
      },
      'aria-label': `${spec.name} for ${row.requestID}`,
      className: `w-full min-w-[110px] px-1.5 py-0.5 rounded border text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 ${invalid ? 'border-red-400' : 'border-slate-300'}`
    };
    if (spec.type === 'enum') {
      const options = [...(spec.options ?? [])];
      if (currentText && !options.includes(currentText)) options.push(currentText);
      return <select {...common} data-id="element-347" data-mp="2vkgwiuyoujys">
          <option value="" data-id="element-348" data-mp="2vk74l1u322qg">—</option>
          {options.map(o => <option key={o} value={o} data-id="element-349" data-mp="30c8istata3j4">{o}</option>)}
        </select>;
    }
    return <input type="text" placeholder={spec.format} {...common} data-id="element-350" data-mp="2mku0kooqa6d4" />;
  }
  return <div className="flex items-center gap-1 group min-w-[80px]" data-id="element-351" data-mp="2rjets45kbps2">
      <button type="button" onClick={onStartEdit} title={invalid && validation && !validation.ok ? validation.error : 'Click to edit'} aria-label={`Edit ${spec.name} for ${row.requestID}`} className={`flex-1 text-left text-xs px-1.5 py-0.5 rounded border whitespace-nowrap min-h-[22px] ${invalid ? 'border-red-300 bg-red-50 text-red-700' : staged ? 'border-amber-300 bg-amber-50 text-amber-800' : overridden ? 'border-indigo-200 bg-indigo-50 text-indigo-800' : 'border-transparent text-slate-700 hover:border-slate-300'}`} data-id="element-352" data-mp="2rjn3xoaawgzk">
        {shownText || <span className="text-slate-300" data-id="element-353" data-mp="2gwql6mfljg9m">—</span>}
      </button>
      {differs && !staged && <span title={`Workfront value changed since this override was saved (now: ${cleanupValueToText(row.base[spec.name]) || 'empty'})`} className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800" data-id="element-354" data-mp="2cw6n9gn788mf">
          Differs
        </span>}
      {overridden && !staged && <button type="button" onClick={onRevertField} title={`Revert to ${cleanupValueToText(row.base[spec.name]) || 'empty'}`} aria-label={`Revert ${spec.name} for ${row.requestID}`} className="p-0.5 text-slate-400 hover:text-indigo-600 transition-colors" data-id="element-355" data-mp="2vnfx5r0vszej">
          <Undo2 size={12} data-id="element-356" data-mp="3coabva5aov76" />
        </button>}
    </div>;
}
function csvEscape(v: CleanupValue | undefined): string {
  const s = cleanupValueToText(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function ExportButton({
  rows,
  fields
}: {
  rows: CleanupRow[];
  fields: CleanupFieldSpec[];
}) {
  const handleExport = () => {
    if (rows.length === 0) return;
    const csv = [['source', ...fields.map(f => f.name)].join(','), ...rows.map(r => [csvEscape(r.overriddenFields.length ? 'overridden' : r.source), ...fields.map(f => csvEscape(r.merged[f.name]))].join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], {
      type: 'text/csv'
    }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `cleanup-data-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <button onClick={handleExport} disabled={rows.length === 0} className="px-3 py-1.5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5" data-id="element-357" data-mp="1tpcdkjzz5neo">
      <Download size={14} data-id="element-358" data-mp="1jpvbecjxlrng" /> Export
    </button>;
}
export function CleanupDataTab({
  dataSource = DEFAULT_DATA_SOURCE,
  actor = null
}: {
  dataSource?: CleanupDataSource;
  actor?: CleanupActor | null;
}) {
  const {
    setRequests: setDashboardRequests
  } = useRequests();
  const [rows, setRows] = useState<CleanupRow[]>([]);
  const [fields, setFields] = useState<CleanupFieldSpec[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [overriddenCount, setOverriddenCount] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [source, setSource] = useState<CleanupSource | 'all'>('all');
  const [overriddenOnly, setOverriddenOnly] = useState(false);

  // Editing — drafts are staged per row until that row's Save.
  const [drafts, setDrafts] = useState<Drafts>({});
  const [editing, setEditing] = useState<{
    id: string;
    field: string;
  } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const setDashboardRequestsRef = useRef(setDashboardRequests);
  setDashboardRequestsRef.current = setDashboardRequests;
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(prev => {
        if (prev === searchInput.trim()) return prev;
        setOffset(0);
        return searchInput.trim();
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);
  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dataSource.list({
        offset,
        limit: PAGE_SIZE,
        search: search || undefined,
        overriddenOnly,
        source
      });
      setRows(data.rows);
      setFields(data.fields);
      setTotalCount(data.totalCount);
      setOverriddenCount(data.overriddenCount);
      setLastSyncedAt(data.lastSyncedAt);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, search, overriddenOnly, source]);
  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  // After any saved change, refresh the dashboard's shared requests so charts and
  // tables elsewhere reflect the override without a page reload (best-effort).
  const refreshDashboard = () => {
    void dataSource.fetchRequests().then(next => {
      if (next && next.length > 0) setDashboardRequestsRef.current(next);
    }).catch(() => {
      /* the Cleanup table already shows the saved state */
    });
  };
  const replaceRow = (updated: CleanupRow) => {
    setRows(prev => {
      const old = prev.find(r => r.requestID === updated.requestID);
      if (old) {
        const delta = (updated.overriddenFields.length > 0 ? 1 : 0) - (old.overriddenFields.length > 0 ? 1 : 0);
        if (delta !== 0) setOverriddenCount(c => Math.max(0, c + delta));
      }
      return prev.map(r => r.requestID === updated.requestID ? updated : r);
    });
  };
  const clearDraft = (id: string) => {
    setDrafts(prev => {
      if (!(id in prev)) return prev;
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    setRowErrors(prev => {
      if (!(id in prev)) return prev;
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
  };
  const stageValue = (row: CleanupRow, field: string, text: string) => {
    const id = row.requestID;
    setDrafts(prev => {
      const rowDraft = {
        ...(prev[id] ?? {})
      };
      if (text === cleanupValueToText(row.merged[field])) delete rowDraft[field];else rowDraft[field] = text;
      const next = {
        ...prev
      };
      if (Object.keys(rowDraft).length === 0) delete next[id];else next[id] = rowDraft;
      return next;
    });
  };
  const cancelFieldDraft = (row: CleanupRow, field: string) => {
    setDrafts(prev => {
      const rowDraft = {
        ...(prev[row.requestID] ?? {})
      };
      delete rowDraft[field];
      const next = {
        ...prev
      };
      if (Object.keys(rowDraft).length === 0) delete next[row.requestID];else next[row.requestID] = rowDraft;
      return next;
    });
  };
  const specByName = useMemo(() => new Map(fields.map(f => [f.name, f])), [fields]);
  const rowDraftValidity = (row: CleanupRow): {
    changes: Record<string, CleanupValue>;
    error: string | null;
  } => {
    const changes: Record<string, CleanupValue> = {};
    const rowDraft = drafts[row.requestID] ?? {};
    for (const [name, text] of Object.entries(rowDraft)) {
      const spec = specByName.get(name);
      if (!spec) continue;
      const result = validateCleanupInput(spec, text, row.base[name]);
      if (!result.ok) return {
        changes,
        error: result.error
      };
      if (!cleanupValuesEqual(result.value, row.merged[name])) changes[name] = result.value;
    }
    return {
      changes,
      error: null
    };
  };
  const handleSave = async (row: CleanupRow) => {
    const {
      changes,
      error: validationError
    } = rowDraftValidity(row);
    if (validationError) {
      setRowErrors(prev => ({
        ...prev,
        [row.requestID]: validationError
      }));
      return;
    }
    if (Object.keys(changes).length === 0) {
      clearDraft(row.requestID);
      return;
    }
    setBusyId(row.requestID);
    try {
      const updated = await dataSource.save(row.requestID, changes, actor);
      replaceRow(updated);
      clearDraft(row.requestID);
      refreshDashboard();
    } catch (err) {
      setRowErrors(prev => ({
        ...prev,
        [row.requestID]: err instanceof Error ? err.message : 'Failed to save'
      }));
    } finally {
      setBusyId(null);
    }
  };
  const handleRevert = async (row: CleanupRow, fieldNames?: string[]) => {
    setBusyId(row.requestID);
    try {
      const updated = await dataSource.revert(row.requestID, fieldNames, actor);
      replaceRow(updated);
      if (!fieldNames) clearDraft(row.requestID);
      refreshDashboard();
    } catch (err) {
      setRowErrors(prev => ({
        ...prev,
        [row.requestID]: err instanceof Error ? err.message : 'Failed to revert'
      }));
    } finally {
      setBusyId(null);
    }
  };
  const columns: SortableColumn<CleanupRow>[] = [{
    key: '__source',
    label: 'Source',
    render: row => <SourceBadge row={row} data-id="element-359" data-mp="19xiivpkhick5" />,
    sortValue: row => row.overriddenFields.length ? 'overridden' : row.source
  }, ...fields.map<SortableColumn<CleanupRow>>(spec => ({
    key: spec.name,
    label: spec.name,
    render: row => <CleanupCell row={row} spec={spec} draft={drafts[row.requestID]?.[spec.name]} editing={editing?.id === row.requestID && editing.field === spec.name} disabled={busyId === row.requestID} onStartEdit={() => setEditing({
      id: row.requestID,
      field: spec.name
    })} onStopEdit={() => setEditing(null)} onChange={text => stageValue(row, spec.name, text)} onCancelDraft={() => cancelFieldDraft(row, spec.name)} onRevertField={() => void handleRevert(row, [spec.name])} data-id="element-360" data-mp="1lc3yerq7ug5g" />,
    sortValue: row => {
      const v = row.merged[spec.name];
      if (spec.type === 'number') return typeof v === 'number' ? v : Number(v) || 0;
      if (spec.type === 'date') return dateSortKey(v, spec.format);
      return cleanupValueToText(v);
    }
  })), {
    key: '__actions',
    label: 'Actions',
    render: row => {
      const hasDraft = Boolean(drafts[row.requestID]);
      const busy = busyId === row.requestID;
      const rowError = rowErrors[row.requestID];
      return <div className="space-y-1 min-w-[130px]" data-id="element-361" data-mp="34a9wtuuwp41v">
            <div className="flex items-center gap-1.5" data-id="element-362" data-mp="393uwrsg2883p">
              {hasDraft && <>
                  <button onClick={() => void handleSave(row)} disabled={busy} aria-label={`Save ${row.requestID}`} className="px-2 py-0.5 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors inline-flex items-center gap-1" data-id="element-363" data-mp="3e14ojeqx8vup">
                    {busy ? <Loader2 size={12} className="animate-spin" data-id="element-364" data-mp="1spv1ius5t6ms" /> : <Save size={12} data-id="element-365" data-mp="1xnmu7r0mows1" />}
                    Save
                  </button>
                  <button onClick={() => clearDraft(row.requestID)} disabled={busy} aria-label={`Discard ${row.requestID}`} className="px-2 py-0.5 rounded-md border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors inline-flex items-center gap-1" data-id="element-366" data-mp="1xphfj6ftaqwn">
                    <X size={12} data-id="element-367" data-mp="28lt48ss64j9z" /> Discard
                  </button>
                </>}
              {!hasDraft && row.overriddenFields.length > 0 && <button onClick={() => void handleRevert(row)} disabled={busy} aria-label={`Revert all ${row.requestID}`} title="Remove all manual overrides for this request" className="px-2 py-0.5 rounded-md border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors inline-flex items-center gap-1" data-id="element-368" data-mp="24jz5wr5g3rcz">
                  {busy ? <Loader2 size={12} className="animate-spin" data-id="element-369" data-mp="36xk50caea416" /> : <Undo2 size={12} data-id="element-370" data-mp="36uo5ijiwf7am" />}
                  Revert all
                </button>}
            </div>
            {rowError && <div className="text-[11px] text-red-600 whitespace-normal" data-id="element-371" data-mp="2wz4b61jzggnz">{rowError}</div>}
          </div>;
    }
  }];
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const unsavedRowCount = Object.keys(drafts).length;
  return <div className="space-y-4" data-id="element-372" data-mp="2n6os10wy9m1i">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-373" data-mp="2s01htedd7yvu">
        <div data-id="element-374" data-mp="2e83drhyigt6j">
          <h3 className="text-base font-semibold text-slate-900" data-id="element-375" data-mp="2e8d2z3bqewb6">Cleanup data</h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-376" data-mp="2j6ogbo18joy5">
            {totalCount} total · {overriddenCount} overridden
            {unsavedRowCount > 0 && ` · ${unsavedRowCount} unsaved`}
            {' · '}
            {lastSyncedAt ? `last synced ${friendlyTime(lastSyncedAt)}` : 'not synced yet'}
          </p>
          <p className="text-xs text-slate-400 mt-0.5" data-id="element-377" data-mp="2or9vticw9m54">
            Click a cell to edit. Manual values override Workfront data and survive re-syncs; use Revert to restore the original.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-378" data-mp="2fpacukibk7zj">
          <ExportButton rows={rows} fields={fields} data-id="element-379" data-mp="2frwmqqsomzkx" />
          <button onClick={() => void loadRows()} disabled={loading} className="px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5" data-id="element-380" data-mp="2apxjri0uu4oi">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} data-id="element-381" data-mp="1wysrx23qagoq" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-id="element-382" data-mp="1ryo9v7n69dwe">
        <div className="grid grid-cols-6 gap-3 items-end" data-id="element-383" data-mp="1n0mmiaipqsf4">
          <div className="col-span-2" data-id="element-384" data-mp="1i21nufj40gss">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-385" data-mp="1i338nbpxghnp">Search</label>
            <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Any field..." className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-386" data-mp="17y4rv8euby5n" />
          </div>
          <div data-id="element-387" data-mp="p8y4v970kke1">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-388" data-mp="p9gcf1z4ml19">Source</label>
            <select value={source} onChange={e => {
            setSource(e.target.value as CleanupSource | 'all');
            setOffset(0);
          }} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-389" data-mp="u7rlhahlkrif">
              <option value="all" data-id="element-390" data-mp="6fayqmmx0bcs">All</option>
              <option value="synced" data-id="element-391" data-mp="bfoy7gagacz7">Synced</option>
              <option value="uploaded" data-id="element-392" data-mp="bedz3try9tef">Uploaded</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 pb-1.5" data-id="element-393" data-mp="23qxhiqpdflt8">
            <input type="checkbox" checked={overriddenOnly} onChange={e => {
            setOverriddenOnly(e.target.checked);
            setOffset(0);
          }} data-id="element-394" data-mp="28mctte7qd9nj" />
            Overridden only
          </label>
        </div>
      </div>

      {/* Error */}
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-id="element-395" data-mp="1kzwmgdrdgaey">{error}</div>}

      {/* Table */}
      {loading && rows.length === 0 ? <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-id="element-396" data-mp="1txyqz3abdl6z">Loading requests...</div> : rows.length === 0 ? <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-id="element-397" data-mp="1yrkwmbi28ota">No requests found.</div> : <div className="overflow-x-auto [&_table]:w-max" data-id="element-398" data-mp="16977r1epcd6c">
          <SortableTable columns={columns} data={rows} className="tbl tbl-wrap" rowKey={row => row.requestID} rowStyle={row => drafts[row.requestID] ? {
        background: '#fffbeb'
      } : undefined} data-id="element-399" data-mp="167medf44j0a2" />
        </div>}

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-between text-sm text-slate-500" data-id="element-400" data-mp="kqezl5z9yfhn">
          <span data-id="element-401" data-mp="fr23dumijw2e">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, totalCount)} of {totalCount}
          </span>
          <div className="flex items-center gap-1" data-id="element-402" data-mp="aztx37ghcqtg">
            <button onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))} disabled={offset === 0} aria-label="Previous page" className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-403" data-mp="ay8y95n4ulps">
              <ChevronLeft size={16} data-id="element-404" data-mp="3t7t4espbudo4" />
            </button>
            <span className="px-2 text-xs" data-id="element-405" data-mp="3o86r6j9i0xfb">
              Page {Math.floor(offset / PAGE_SIZE) + 1} of {totalPages}
            </span>
            <button onClick={() => setOffset(o => Math.min((totalPages - 1) * PAGE_SIZE, o + PAGE_SIZE))} disabled={offset >= (totalPages - 1) * PAGE_SIZE} aria-label="Next page" className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-406" data-mp="3elm7e58bzay3">
              <ChevronRight size={16} data-id="element-407" data-mp="15occ5wnjetg" />
            </button>
          </div>
        </div>}
    </div>;
}